import { useParams, Navigate, Link } from 'react-router-dom'
import { CalendarDays, GraduationCap, Backpack, ArrowLeft, Loader2 } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { useCamps } from '../hooks/useCamps'
import { getCampsPath, getRegistrationPath } from '../utils/routes'
import Button from '../components/ui/Button'
import PlaceholderImage from '../components/ui/PlaceholderImage'
import AvailabilityBadge from '../components/ui/AvailabilityBadge'
import SEOHead from '../components/SEOHead'

export default function CampDetail() {
  const { locale, t } = useLocale()
  const { slug } = useParams()
  const { camps, loading } = useCamps()
  const camp = camps.find((c) => c.id === slug)

  // Still loading the CMS data — wait before deciding it's a 404.
  if (loading && !camp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <Loader2 className="animate-spin text-primary" size={40} aria-label="Loading" />
      </div>
    )
  }

  // Unknown / inactive camp → send to the hub.
  if (!camp || !camp.active) {
    return <Navigate to={getCampsPath(locale)} replace />
  }

  const title = camp.title[locale]
  const isFull = !!camp.availability && camp.availability.available <= 0
  const registrationPath = getRegistrationPath(locale)

  return (
    <>
      <SEOHead title={`${title} — Magic Harghita Adventures`} description={camp.shortDescription[locale]} />

      <article className="section-padding bg-white">
        <div className="container-max max-w-4xl">
          <Link
            to={getCampsPath(locale)}
            className="inline-flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-primary transition-colors mb-6"
          >
            <ArrowLeft size={16} />
            {t.camps.backToCamps}
          </Link>

          <div className="flex items-start justify-between gap-3">
            <h1>{title}</h1>
            <AvailabilityBadge availability={camp.availability} locale={locale} />
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent text-primary text-sm font-medium rounded-full">
              <CalendarDays size={16} />
              {camp.dates[locale]}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface text-text-secondary text-sm font-medium rounded-full border border-border">
              <GraduationCap size={16} />
              {camp.category[locale]}
            </span>
          </div>

          <div className="mt-6">
            {camp.image && !camp.image.includes('placeholder') ? (
              <img
                src={camp.image}
                alt={title}
                width={1280}
                height={720}
                loading="lazy"
                className="w-full aspect-video object-cover rounded-card"
              />
            ) : (
              <PlaceholderImage label={title} variant="camp" className="w-full aspect-video rounded-card" />
            )}
          </div>

          <p className="mt-6 text-text-secondary leading-relaxed">{camp.shortDescription[locale]}</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading font-medium text-lg text-secondary mb-3">{t.modal.activities}</h2>
              <ul className="list-disc list-inside space-y-1 text-text-secondary text-sm">
                {camp.activities[locale].map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading font-medium text-lg text-secondary mb-3 flex items-center gap-2">
                <Backpack size={18} className="text-primary" />
                {t.modal.packingList}
              </h2>
              <ul className="list-disc list-inside space-y-1 text-text-secondary text-sm">
                {camp.packingList[locale].map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-accent rounded-card">
            <div className="text-center sm:text-left">
              <p className="text-sm text-text-secondary">{t.modal.price}</p>
              <p className="text-2xl font-heading font-semibold text-primary">{camp.price}</p>
            </div>
            {isFull ? (
              <Button size="lg" disabled>
                {locale === 'hu' ? 'Betelt' : 'Complet'}
              </Button>
            ) : (
              <Button to={`${registrationPath}?camp=${camp.id}`} size="lg">
                {t.modal.register}
              </Button>
            )}
          </div>
        </div>
      </article>
    </>
  )
}
